import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Sheet,
  Box,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Typography,
} from '@mui/joy';
import {
  Hospital,
  Menu as MenuIcon,
  X as CloseIcon,
  User,
  LogOut,
  Moon,
  Sun,
} from 'lucide-react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, useColorScheme } from '@mui/joy/styles';

function ThemeToggle({ iconColor }) {
  const { mode, setMode } = useColorScheme();

  return (
    <IconButton
      size="sm"
      variant="plain"
      color="primary"
      onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
      aria-label="Toggle light/dark theme"
      sx={{ color: iconColor }}
    >
      {mode === 'light' ? (
        <Moon size={16} style={{ fill: iconColor, color: iconColor }} />
      ) : (
        <Sun size={16} style={{ fill: '#facc15', color: '#facc15' }} />
      )}
    </IconButton>
  );
}

export default function Header({ open, setOpen }) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary[500];
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorElOrgs, setAnchorElOrgs] = useState(null);
  const openOrgsMenu = Boolean(anchorElOrgs);
  const menuRefOrgs = useRef(null);

  const storedUser = useMemo(() => {
    const raw = localStorage.getItem('username');
    return raw ? JSON.parse(raw) : null;
  }, []);

  const fullName = storedUser?.nome || 'Utente';

  const isSuperadmin =
    storedUser?.is_superadmin === true ||
    storedUser?.is_superadmin === 'true';

  const hasValidOrgPermission = (ente) => {
    const permessi = Array.isArray(ente?.permessi_ente)
      ? ente.permessi_ente
      : [];

    return permessi.some((permesso) => Number(permesso.menu_id) !== 9);
  };

  const [orgs, setOrgs] = useState([]);
  const [orgNames, setOrgNames] = useState({});
  const [selectedOrgs, setSelectedOrgs] = useState(() => {
    const saved = localStorage.getItem('selectedOrg');
    return saved || null;
  });

  useEffect(() => {
    async function loadOrgs() {
      if (!storedUser) return;

      try {
        if (isSuperadmin) {
          const res = await fetch(`${process.env.REACT_APP_SERVER_HOST}/org/active`, {
            credentials: 'include',
          });

          if (!res.ok) throw new Error('Errore nel recupero enti');

          const data = await res.json();
          const ids = data.map((org) => String(org.id));

          setOrgs(ids);

          if (!selectedOrgs || !ids.includes(selectedOrgs)) {
            setSelectedOrgs(ids[0] || null);
          }

          const nameMap = {};
          data.forEach((org) => {
            nameMap[String(org.id)] = org.name;
          });

          setOrgNames(nameMap);
        } else {
          const filtered = (storedUser.enti || []).filter(
            (e) =>
              Number(e.stato_ente) === 1 &&
              hasValidOrgPermission(e)
          );

          const ids = filtered.map((e) => String(e.id_ente));

          setOrgs(ids);

          if (!selectedOrgs || !ids.includes(selectedOrgs)) {
            setSelectedOrgs(ids[0] || null);
          }

          const nameMap = {};
          filtered.forEach((ente) => {
            nameMap[String(ente.id_ente)] =
              ente.nome_ente || `Ente ${ente.id_ente}`;
          });

          setOrgNames(nameMap);
        }
      } catch (err) {
        console.error('Errore caricamento enti:', err);
        setOrgNames({});
        setOrgs([]);
        setSelectedOrgs(null);
      }
    }

    loadOrgs();
  }, [storedUser, isSuperadmin, selectedOrgs]);

  useEffect(() => {
    if (selectedOrgs) {
      localStorage.setItem('selectedOrg', selectedOrgs);
      localStorage.setItem(
        'selectedOrgName',
        orgNames[selectedOrgs] || ''
      );
    }
  }, [selectedOrgs, orgNames]);

  const handleOrgsClick = (e) => setAnchorElOrgs(e.currentTarget);
  const handleOrgsClose = () => setAnchorElOrgs(null);

  const handleSelectOrg = (orgId) => {
    setSelectedOrgs(orgId);
    localStorage.setItem('selectedOrg', orgId);
    localStorage.setItem('selectedOrgName', orgNames[orgId] || '');
    setAnchorElOrgs(null);
    window.location.reload();
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  useEffect(() => {
    if (!openOrgsMenu) return;

    function handleClickOutside(event) {
      if (
        anchorElOrgs &&
        !anchorElOrgs.contains(event.target) &&
        menuRefOrgs.current &&
        !menuRefOrgs.current.contains(event.target)
      ) {
        setAnchorElOrgs(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openOrgsMenu, anchorElOrgs]);

  return (
    <Sheet
      sx={{
        px: 2,
        py: 0.5,
        minHeight: '55px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        bgcolor: 'background.surface',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, maxWidth: 500 }}>
        {isSmallScreen && (
          <IconButton
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Chiudi menu' : 'Apri menu'}
            size="sm"
          >
            {open ? (
              <CloseIcon size={20} style={{ color: primaryColor }} />
            ) : (
              <MenuIcon size={20} style={{ color: primaryColor }} />
            )}
          </IconButton>
        )}

        <Input disabled placeholder="Cerca..." size="sm" sx={{ flexGrow: 1, minWidth: 0 }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
        <ThemeToggle iconColor={primaryColor} />

        <IconButton
          size="sm"
          color="primary"
          onClick={handleOrgsClick}
          sx={{ px: 1 }}
          aria-controls={openOrgsMenu ? 'orgs-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openOrgsMenu ? 'true' : undefined}
        >
          <Hospital size={20} strokeWidth={1.5} style={{ color: primaryColor }} />

          {!isSmallScreen && (
            <Typography level="body-sm" ml={1}>
              {orgNames[selectedOrgs] || 'Seleziona ente'}
            </Typography>
          )}
        </IconButton>

        <Menu
          id="orgs-menu"
          anchorEl={anchorElOrgs}
          open={openOrgsMenu}
          onClose={handleOrgsClose}
          placement="bottom-end"
          modifiers={[{ name: 'offset', options: { offset: [5, 15] } }]}
          slotProps={{
            root: { sx: { minWidth: 160, mt: 1 }, ref: menuRefOrgs },
          }}
        >
          {orgs.length > 0 ? (
            orgs.map((orgId) => (
              <MenuItem
                key={orgId}
                selected={orgId === selectedOrgs}
                onClick={() => handleSelectOrg(orgId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  ...(orgId === selectedOrgs && { bgcolor: 'primary.softBg' }),
                }}
              >
                <Hospital size={18} strokeWidth={1.5} style={{ color: primaryColor }} />
                <Typography level="body2">
                  {orgNames[orgId] || `Ente ${orgId}`}
                </Typography>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography level="body2">Nessun ente disponibile</Typography>
            </MenuItem>
          )}
        </Menu>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <User size={18} style={{ color: primaryColor }} />

          <Typography level="body-sm" fontWeight="md">
            {fullName}
          </Typography>

          <IconButton
            size="sm"
            color="danger"
            onClick={handleLogout}
            aria-label="Logout"
          >
            <LogOut size={20} strokeWidth={1.5} />
          </IconButton>
        </Box>
      </Box>
    </Sheet>
  );
}